/**
 * The reader: a FOURTH COLUMN, beside the answer and the evidence.
 *
 * It has been wrong twice. First a new tab, which replaces the answer with the
 * document. Then a modal drawer, which shaded the app and sat on top of it — still
 * "the document INSTEAD of the answer", just with a scrim. Both break the one move
 * this product exists for: reading the claim and its source at the same time.
 *
 * So it is a real pane. The layout goes rail | reading column | evidence | reader, and
 * the other three stay live and usable while it is open. Nothing is dimmed, because
 * nothing is disabled.
 *
 * Not a dialog, therefore: no `aria-modal`, no scrim, no focus trap — those would all
 * be lies about a pane you can click past. Escape still closes it, because that is
 * what Escape means, and it is labelled as a region so a screen reader can reach it.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/lib/api.ts";
import type { Citation } from "@/lib/api-types.ts";
import { Button } from "./primitives.tsx";
import { originalLanding, pdfUrlAtPage, splitTextRange } from "./readerLanding.ts";

export interface ReaderTarget {
  citation: Citation;
  /** The chunk text, when the stream carried it. */
  passage?: string;
  /**
   * WHICH of the two things the reader was asked for.
   *
   * `"passage"` — the chunk the answer actually leaned on. Opened by clicking the
   * citation itself, and available even when the original file is not.
   * `"original"` — the source document as published. Opened by "Buka dokumen asli".
   *
   * They are different questions ("what did it quote?" vs "what does the document
   * say?") and were previously answered by one screen showing both, which made the
   * original look like an afterthought under the chunk.
   */
  mode: "passage" | "original";
}

type Original =
  | { state: "idle" }
  | { state: "loading" }
  /** A format the browser renders in place: PDF, image, plain text, HTML. */
  | { state: "ready"; url: string; mediaType: string }
  /** The row exists, the bytes do not — a real state, not an error to swallow. */
  | { state: "absent"; detail: string };

export function DocumentReader({
  target,
  onClose,
  onOpenOriginal,
}: {
  target: ReaderTarget | null;
  onClose: () => void;
  /** Switch this same pane from the chunk to the published file. */
  onOpenOriginal?: () => void;
}) {
  const { t } = useTranslation();
  const [original, setOriginal] = useState<Original>({ state: "idle" });
  /** Fetched only when the turn did not carry the text — i.e. a replayed conversation. */
  const [fetchedPassage, setFetchedPassage] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  const open = target !== null;
  const documentId = target?.citation.document_id;

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    panel.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  // Fetched only when the ORIGINAL was asked for, and released on close — an object
  // URL held after that is a multi-megabyte leak per document viewed. Opening a chunk
  // pulls no bytes at all.
  useEffect(() => {
    if (!open || !documentId || target?.mode !== "original") return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setOriginal({ state: "loading" });

    void (async () => {
      try {
        const response = await fetch(api.documentContentUrl(documentId), {
          credentials: "same-origin",
        });
        if (cancelled) return;
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { detail?: string } | null;
          setOriginal({
            state: "absent",
            detail: typeof body?.detail === "string" ? body.detail : t("reader.absentBody"),
          });
          return;
        }
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setOriginal({ state: "ready", url: objectUrl, mediaType: blob.type });
      } catch {
        if (!cancelled) setOriginal({ state: "absent", detail: t("errors.network") });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, documentId, t, target?.mode]);

  // A REPLAYED turn has citations but no passage text: `source_citations` records
  // which chunk, never its words. Fetch them, so a citation in a conversation you come
  // back to is still readable rather than an empty promise.
  const chunkId = target?.citation.chunk_id;
  const needsPassage = open && target?.mode === "passage" && !target.passage && !!chunkId;
  useEffect(() => {
    if (!needsPassage || !chunkId) return;
    let cancelled = false;
    setFetchedPassage(null);
    void api
      .chunk(chunkId)
      .then((chunk) => {
        if (!cancelled) setFetchedPassage(chunk.text);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [needsPassage, chunkId]);

  const passageText = target?.passage ?? fetchedPassage ?? null;
  const citation = target?.citation;
  const location = citation
    ? [
        citation.legal_reference,
        citation.page_number !== null ? t("evidence.page", { page: citation.page_number }) : null,
        citation.section_path.length > 0 ? citation.section_path.join(" › ") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  if (!open) return null;

  return (
    <aside
        className="reader"
        role="region"
        aria-label={citation?.document_title ?? t("reader.title")}
        tabIndex={-1}
        ref={panel}
      >
        {citation ? (
          <>
            <div className="reader-head">
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="col-title">{citation.document_title}</div>
                {location ? <div className="ev-loc">{location}</div> : null}
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} aria-label={t("common.close")}>
                ✕
              </Button>
            </div>

            <div className="reader-body">
              {/* THE CHUNK. What the answer actually leaned on — available even when
                  the original file is not, because it came down the stream. */}
              {target?.mode === "passage" ? (
                <section aria-label={t("reader.passage")}>
                  <div className="eyebrow">{t("reader.passage")}</div>
                  {passageText ? (
                    <div className="doc" style={{ marginTop: "var(--s2)" }}>
                      <mark>{passageText}</mark>
                    </div>
                  ) : (
                    <p className="hint">{t("common.loading")}</p>
                  )}
                  <div style={{ marginTop: "var(--s5)" }}>
                    {/* One step to the other question, rather than both at once. */}
                    <Button size="sm" onClick={() => onOpenOriginal?.()}>
                      {t("evidence.openDocument")}
                    </Button>
                  </div>
                </section>
              ) : null}

              {target?.mode === "original" ? (
                <>
                  <div className="eyebrow">{t("reader.original")}</div>
                  {original.state === "loading" ? (
                <p className="hint">{t("common.loading")}</p>
              ) : original.state === "absent" ? (
                // Not an error panel: in a copied environment this is the EXPECTED
                // state, and dressing it as a failure would send people debugging a
                // system that is behaving correctly.
                <div className="panel panel-slate" style={{ marginTop: "var(--s2)" }}>
                  <div className="panel-title">{t("reader.absentTitle")}</div>
                  <div className="panel-body">{original.detail}</div>
                </div>
                  ) : original.state === "ready" ? (
                    <ReaderBody
                      url={original.url}
                      mediaType={original.mediaType}
                      title={citation.document_title}
                      citation={citation}
                      locationLabel={location}
                    />
                  ) : null}
                </>
              ) : null}
            </div>
          </>
        ) : null}
    </aside>
  );
}

function ReaderBody({
  url,
  mediaType,
  title,
  citation,
  locationLabel,
}: {
  url: string;
  mediaType: string;
  title: string;
  citation: Citation;
  locationLabel: string;
}) {
  const { t } = useTranslation();

  // Where inside the original this citation has a PROVEN location — a 1-based
  // original page for PDFs, a char range for plain text, or nothing. `unlocated`
  // renders an explicit notice: opening at the top while implying it is the cited
  // passage would be a silent lie about provenance (#533).
  const landing = originalLanding(citation, mediaType);

  // The browser renders PDFs and images natively. Bundling a PDF engine would be a
  // second renderer to keep correct, for a document the platform already draws.
  if (mediaType.includes("pdf")) {
    const pageUrl = landing.kind === "pdf-page" ? pdfUrlAtPage(url, landing.page) : url;
    return (
      <>
        {landing.kind === "pdf-page" ? (
          <p className="hint">
            {t("reader.landedPage", { page: landing.page })}
            {locationLabel ? ` · ${locationLabel}` : ""}
          </p>
        ) : (
          <NoLocationNotice locationLabel={locationLabel} />
        )}
        <object data={pageUrl} type="application/pdf" className="reader-frame" aria-label={title}>
          <p className="hint">{t("reader.cannotEmbed")}</p>
        </object>
      </>
    );
  }
  if (mediaType.startsWith("image/")) {
    // An OCR'd page image IS the cited page when the extraction was per-page; its
    // identity is already stated by the location line in the reader header.
    return <img src={url} alt={title} className="reader-image" />;
  }
  if (mediaType.startsWith("text/") || mediaType.includes("json")) {
    return (
      <TextOriginal
        url={url}
        range={landing.kind === "text-range" ? landing : null}
        locationLabel={locationLabel}
      />
    );
  }

  // A spreadsheet or a binary the browser cannot draw. Offer it rather than pretend.
  return (
    <div className="panel panel-slate" style={{ marginTop: "var(--s2)" }}>
      <div className="panel-title">{t("reader.notRenderable")}</div>
      <div className="panel-body">
        <a href={url} download={title} className="btn btn-sm">
          {t("reader.download")}
        </a>
      </div>
    </div>
  );
}

/** The honest fallback: the file is shown, but the exact cited spot is not known. */
function NoLocationNotice({ locationLabel }: { locationLabel: string }) {
  const { t } = useTranslation();
  return (
    <p className="hint" role="note">
      {t("reader.noLocation")}
      {locationLabel ? ` · ${locationLabel}` : ""}
    </p>
  );
}

/** Plain text is read in the document's own voice, not as a code block. */
function TextOriginal({
  url,
  range,
  locationLabel,
}: {
  url: string;
  /** The cited char range, ONLY when its identity against these bytes is proven. */
  range: { start: number; end: number } | null;
  locationLabel: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState<string | null>(null);
  const citedMark = useRef<HTMLElement>(null);
  useEffect(() => {
    let cancelled = false;
    void fetch(url)
      .then((response) => response.text())
      .then((body) => {
        if (!cancelled) setText(body);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [url]);

  // splitTextRange refuses (null) when the range does not fit these bytes — absent
  // content, a truncated file, an offset drift. Refusal falls through to the full
  // text WITH the explicit notice, never to a wrong highlight.
  const highlighted = text !== null && range ? splitTextRange(text, range.start, range.end) : null;

  useEffect(() => {
    if (highlighted) citedMark.current?.scrollIntoView({ block: "center" });
  }, [highlighted]);

  if (text === null) return null;
  if (highlighted) {
    return (
      <>
        <p className="hint">
          {t("reader.citedRange")}
          {locationLabel ? ` · ${locationLabel}` : ""}
        </p>
        <pre className="doc reader-text">
          {highlighted.before}
          <mark ref={citedMark}>{highlighted.cited}</mark>
          {highlighted.after}
        </pre>
      </>
    );
  }
  return (
    <>
      <NoLocationNotice locationLabel={locationLabel} />
      <pre className="doc reader-text">{text}</pre>
    </>
  );
}
